export type MoodLevel = 'great' | 'good' | 'okay' | 'bad' | 'awful';

export interface MoodEntry {
  date: string; // YYYY-MM-DD
  mood: MoodLevel;
  note?: string;
}

export const MOOD_CONFIG: Record<MoodLevel, { emoji: string; label: string; color: string; bgColor: string; deepColor: string }> = {
  great: { emoji: '😄', label: 'Great', color: 'var(--mood-great)', bgColor: 'var(--mood-great-bg)', deepColor: 'var(--mood-great-deep)' },
  good:  { emoji: '🙂', label: 'Good',  color: 'var(--mood-good)',  bgColor: 'var(--mood-good-bg)',  deepColor: 'var(--mood-good-deep)' },
  okay:  { emoji: '😐', label: 'Okay',  color: 'var(--mood-okay)',  bgColor: 'var(--mood-okay-bg)',  deepColor: 'var(--mood-okay-deep)' },
  bad:   { emoji: '😟', label: 'Bad',   color: 'var(--mood-bad)',   bgColor: 'var(--mood-bad-bg)',   deepColor: 'var(--mood-bad-deep)' },
  awful: { emoji: '😢', label: 'Awful', color: 'var(--mood-awful)', bgColor: 'var(--mood-awful-bg)', deepColor: 'var(--mood-awful-deep)' },
};

export const MOOD_LEVELS: MoodLevel[] = ['great', 'good', 'okay', 'bad', 'awful'];

const STORAGE_KEY = 'mood-journal-entries';

// Generate mock entries for the past few weeks
function generateMockData(): Record<string, MoodEntry> {
  const entries: Record<string, MoodEntry> = {};
  const today = new Date();

  const mockEntries: Array<{ daysAgo: number; mood: MoodLevel; note?: string }> = [
    { daysAgo: 0, mood: 'good', note: 'Productive morning, finished the report' },
    { daysAgo: 1, mood: 'great', note: 'Coffee with Sarah — so good to catch up!' },
    { daysAgo: 2, mood: 'okay' },
    { daysAgo: 3, mood: 'good', note: 'Nice walk in the park after work' },
    { daysAgo: 4, mood: 'bad', note: 'Slept poorly, headache all day' },
    { daysAgo: 5, mood: 'okay', note: 'Quiet day at home' },
    { daysAgo: 6, mood: 'great', note: 'Beach day! Perfect weather 🌊' },
    { daysAgo: 7, mood: 'good' },
    { daysAgo: 9, mood: 'okay', note: 'Monday blues but got through it' },
    { daysAgo: 10, mood: 'great', note: 'Got a promotion at work!' },
    { daysAgo: 11, mood: 'good', note: 'Tried a new recipe for dinner' },
    { daysAgo: 13, mood: 'awful', note: 'Argument with a friend, feeling down' },
    { daysAgo: 14, mood: 'bad', note: 'Still thinking about yesterday' },
    { daysAgo: 15, mood: 'okay' },
    { daysAgo: 17, mood: 'good', note: 'Movie night — watched something great' },
    { daysAgo: 18, mood: 'great', note: 'Morning run, felt amazing!' },
    { daysAgo: 20, mood: 'good' },
    { daysAgo: 22, mood: 'okay', note: 'Meh kind of day' },
    { daysAgo: 24, mood: 'good', note: 'Read a whole book in one sitting' },
    { daysAgo: 26, mood: 'great', note: 'Family dinner, laughed a lot' },
    { daysAgo: 28, mood: 'bad', note: 'Overslept and missed gym' },
    { daysAgo: 30, mood: 'good' },
    { daysAgo: 33, mood: 'okay', note: 'Average day, nothing special' },
    { daysAgo: 35, mood: 'great', note: 'Started learning guitar!' },
    { daysAgo: 38, mood: 'good', note: 'Cooked for friends' },
    { daysAgo: 40, mood: 'awful', note: 'Migraine all day, stayed in bed' },
    { daysAgo: 42, mood: 'okay' },
    { daysAgo: 45, mood: 'good', note: 'Garden is looking great' },
  ];

  mockEntries.forEach(({ daysAgo, mood, note }) => {
    const date = new Date(today);
    date.setDate(date.getDate() - daysAgo);
    const key = formatDate(date);
    entries[key] = { date: key, mood, note };
  });

  return entries;
}

export function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

export function loadEntries(): Record<string, MoodEntry> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // ignore parse errors
  }
  // Seed with mock data on first visit
  const mock = generateMockData();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(mock));
  return mock;
}

export function saveEntry(entry: MoodEntry): Record<string, MoodEntry> {
  const entries = loadEntries();
  entries[entry.date] = entry;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  return entries;
}

export function getMonthDays(year: number, month: number): Date[] {
  const days: Date[] = [];
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  // Fill in leading empty days (for alignment starting on Sunday)
  const startDow = firstDay.getDay();
  for (let i = startDow - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push(d);
  }

  // Fill in the month
  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push(new Date(year, month, d));
  }

  // Fill trailing days to complete the grid (always 42 cells = 6 rows)
  while (days.length < 42) {
    const d = new Date(year, month + 1, days.length - startDow - lastDay.getDate() + 1);
    days.push(d);
  }

  return days;
}

export function getMonthName(month: number): string {
  return ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'][month];
}

export function getDayName(day: number): string {
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day];
}
